import React, { useContext, useEffect, useState } from "react";
import { Actions, ContentContainer, Grid, ProductSummary, SummaryLabels, WhiteContainer } from "../../../../../styles/orderform.tsx";
import AddressForm from "../Common/AddressForm.tsx";
import { OrderContext } from "../../OrderContext.tsx";
import { getAdditionalService, getPeriodNumberFromLabel, getVariation, isReferenceCode } from "../../../../../misc/helpers.ts";
import { einvoiceBanks, invoiceTypes, contractTypes, emailRegex, phoneRegex, addressRegex } from "../../data.ts";
import dayjs from "dayjs";
import Terms from "../Common/Terms.tsx";
import { DiscountRow, Order, ValidationErrors } from "../../types.ts";
import TextInput from "../b2b/TextInput.tsx";
import { beginCheckoutEvent } from "../../../../../misc/analytics.ts";
import DateInput from "../Common/DateInput.tsx";
import ContractTypeText from "../Common/ContractTypeText.tsx";
import IconCheckCircle from "../../../../../assets/images/icon-check-circle.svg";
import CheckIcon from "../../../../../assets/images/icon-check.svg?react";
import { useAppTranslation } from "../../../../../translations/useAppTranslation.ts";


function PhaseFour() {
  const { formValues, handleFormChange, products, addOns, confirmOrder, discountInfo, discountRows } = useContext(OrderContext);

  const { t, db_t } = useAppTranslation();

  const [selectedProduct] = useState(getVariation({ products, contractTemplateId: formValues['product'] }));
  const [selectedAddon] = useState(getAdditionalService(formValues['additional-services'], addOns));
  const [minDate, setMinDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [maxDate, setMaxDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [monthlyPrice] = useState(selectedProduct ? (selectedProduct.monthly_price + (selectedAddon?.monthly_price ?? 0)) : 0);
  const [formErrors, setFormErrors] = useState<ValidationErrors>({});
  const [allFormErrors, setAllFormErrors] = useState<ValidationErrors>({});
  const [showErrors, setShowErrors] = useState(false);
  const [showButtons, setShowButtons] = useState(true);
  const [discountText, setDiscountText] = useState<string | undefined>(undefined);
  const [energyName, setEnergyName] = useState('common.energyPrice');
  const [discountRow, setDiscountRow] = useState<DiscountRow | undefined>(undefined);
  const [contractPeriod, setContractPeriod] = useState<number | undefined>(undefined);

  const confirmClick = () => {
    if (!selectedProduct) {
      return
    }
    const errors = validateFourthPhase(true);
    if (Object.keys(errors).length === 0) {
      beginCheckoutEvent(formValues, selectedProduct); // Analytics
      confirmOrder();
      setShowButtons(false);
    } else {
      setFormErrors(errors);
      setShowErrors(true)
      const errorElements = document.querySelectorAll('.invalid');
      // scroll to the first error element
      if (errorElements.length > 0) {
        const firstErrorElement = errorElements[0] as HTMLElement;
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  const getFieldsFormattedErrors = () => {
    // Do not check required errors here, just check if the fields are formatted correctly
    const formattedErrors: ValidationErrors = {};
    if (formValues['email'] && !emailRegex.test(formValues['email'])) {
      formattedErrors['email'] = 'Sähköpostiosoite on virheellinen';
    }
    if (formValues['phone'] && !phoneRegex.test(formValues['phone'])) {
      formattedErrors['phone'] = 'Puhelinnumero on virheellinen';
    }

    if (formValues['location-house']) {
      if (!addressRegex.test(formValues['location-house'])) {
        formattedErrors['location-house'] = 'Tarkista tiedot';
      }
    }
    if (formValues['invoice-house']) {
      const houseRegex = /^[a-zA-Z0-9.\-\/]+$/;
      if (!houseRegex.test(formValues['invoice-house'])) {
        formattedErrors['invoice-house'] = 'Tarkista tiedot';
      }
    }
    const postalCodeRegex = /^[0-9]{5}$/;
    if (formValues['location-postalcode']) {
      if (!postalCodeRegex.test(formValues['location-postalcode'])) {
        formattedErrors['location-postalcode'] = 'Postinumero on virheellinen';
      }
    }
    if (formValues['invoice-postalcode']) {
      if (!postalCodeRegex.test(formValues['invoice-postalcode'])) {
        formattedErrors['invoice-postalcode'] = 'Postinumero on virheellinen';
      }
    }
    if (formValues['location-stairway'] && !addressRegex.test(formValues['location-stairway'])) {
      formattedErrors['location-stairway'] = 'Tarkista tiedot';
    }
    if (formValues['invoice-stairway'] && !addressRegex.test(formValues['invoice-stairway'])) {
      formattedErrors['invoice-stairway'] = 'Tarkista tiedot';
    }
    if (formValues['location-flat'] && !addressRegex.test(formValues['location-flat'])) {
      formattedErrors['location-flat'] = 'Tarkista tiedot';
    }
    if (formValues['invoice-flat'] && !addressRegex.test(formValues['invoice-flat'])) {
      formattedErrors['invoice-flat'] = 'Tarkista tiedot';
    }


    if (formValues['contract-startdate']) {
      const dateString = formValues['contract-startdate'];
      const [day, month, year] = dateString.split('.');
      const parsedDate = dayjs(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)));
      // Check if the date is valid
      if (!parsedDate.isValid()) {
        formattedErrors['contract-startdate'] = 'Päivämäärä on virheellinen';
      } else {
        // Check if the date is in the future
        const today = dayjs();
        if (parsedDate.isBefore(today)) {
          formattedErrors['contract-startdate'] = 'Päivämäärä ei voi olla menneisyydessä';
        }
        // Check if the date is before the minimum date
        if (parsedDate.isBefore(minDate)) {
          const minD = dayjs(minDate).format('DD.MM.YYYY');
          formattedErrors['contract-startdate'] = `Sopimuksen alkamispäivä pitää olla vähintään ${minD}`;
        }
        // Check if the date is after the maximum date
        if (parsedDate.isAfter(maxDate)) {
          const maxD = dayjs(maxDate).format('DD.MM.YYYY');
          formattedErrors['contract-startdate'] = `Sopimuksen alkamispäivä ei voi olla myöhemmin kuin ${maxD}`;
        }
      }
    }
    return formattedErrors;
  }

  const getRequiredFieldsErrors = () => {
    const requiredFields = [
      'email', 'phone', 'accept-terms', 'contract-type', 'contract-startdate', 'invoice-method',
      'location-street', 'location-house', 'location-postalcode', 'location-city', 'location-status'
    ];
    const errors: ValidationErrors = {};
    requiredFields.forEach(field => {
      if (!formValues[field as keyof Order]) {
        errors[field] = 'Pakollinen tieto';
      }
    });
    if (formValues['invoice-method'] === 'einvoice' && !formValues['einvoice-bank']) {
      errors['einvoice-bank'] = 'Pakollinen tieto';
    }
    if (formValues['separated-invoicing-address'] === 'on') {
      const invoiceFields = [
        'invoice-street', 'invoice-house', 'invoice-postalcode', 'invoice-city'
      ];
      invoiceFields.forEach(field => {
        if (!formValues[field as keyof Order]) {
          errors[field] = 'Pakollinen tieto';
        }
      });
    }
    return errors;
  }

  const validateFourthPhase = (all = false) => {
    // Check that all required fields are filled
    let validationErrors: ValidationErrors = {};

    validationErrors = { ...getFieldsFormattedErrors() };
    if (all || showErrors) {
      validationErrors = { ...validationErrors, ...getRequiredFieldsErrors() };
    }
    return validationErrors;
  }

  useEffect(() => {
    if (showErrors) {
      // Validate fourth phase when any of the fields change
      const err = validateFourthPhase();
      setFormErrors(err);
    }
    const allErrors = validateFourthPhase(true);
    setAllFormErrors(allErrors);
    // Input change listener to validate fields on change
    const handleInputChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.type === 'checkbox' || target.type === 'radio') {
        const errors = validateFourthPhase(true);
        if (showErrors) {
          setFormErrors(errors);
        }
      }
    }

    // Blur event listener to validate fields when focus is lost
    const handleBlur = (event: FocusEvent) => {
      const errors = validateFourthPhase();
      setFormErrors(errors);
    }
    document.addEventListener('focusout', handleBlur);
    document.addEventListener('change', handleInputChange);
    return () => {
      document.removeEventListener('focusout', handleBlur);
      document.removeEventListener('change', handleInputChange);
    };
  }, [formValues, showErrors]);

  useEffect(() => {
    let name = 'common.energyPrice';
    if (selectedProduct?.name.includes('rssis')) {
      name = 'common.margin';
    } else if (selectedProduct?.name.includes('ynergia')) {
      name = 'common.priceForecast';
    }
    setEnergyName(name);
    setDiscountRow(discountRows.find(row => row.productId === selectedProduct?.hs_id));
    setContractPeriod(getPeriodNumberFromLabel(selectedProduct?.contract_period.label ?? ''));
    setDiscountText(db_t(discountInfo, 'campaign_text') || db_t(selectedProduct, 'campaign_text'));
  }, [selectedProduct, discountInfo]);

  return (
    <>
      <ContentContainer>
        <h3>{t('phases.summary')}</h3>
      </ContentContainer>

      <WhiteContainer>
        <ProductSummary>
          {discountInfo?.valid && (
            <div className="summary-label discount">
              <CheckIcon height={16} width={16} />
              {isReferenceCode(discountInfo.discount_code ?? '')
                ? 'Suosittelijakoodi käytössä'
                : t('phaseFour.codeInUse', { code: discountInfo.discount_code })
              }
            </div>
          )}
          {selectedProduct && <div className="title">{db_t(selectedProduct, 'name')} </div>}
          {((selectedProduct?.description_in_summary && selectedProduct?.description) || selectedProduct?.campaign_text) && <div className="description summary-description">
            {(selectedProduct && selectedProduct.description_in_summary)
              ? <div dangerouslySetInnerHTML={{ __html: db_t(selectedProduct, 'description') }}></div>
              : null}
            {!!discountText && <div dangerouslySetInnerHTML={{ __html: discountText }}></div>}
          </div>}
          {selectedProduct && <div className="price-row">
            <span className="text">{t('common.monthlyPrice')}</span>
            <b>{(discountRow && (contractPeriod == null || discountRow.duration <= contractPeriod)) ? '0' : selectedProduct.monthly_price } {t('common.€/month')} {(discountRow && discountRow.duration != contractPeriod) ? `${discountRow.duration} ${t('common.months')}` : ''}</b>
          </div>}
          {(discountRow && discountRow.duration != contractPeriod) && <div className="price-row"><span className="text"></span><span>(norm. {selectedProduct?.monthly_price} {t('common.€/month')})</span></div>}
          {selectedProduct && <div className="price-row"><span className="text">{t(energyName)}</span> <b>{selectedProduct.energy_price} {t('common.c/kWh')}</b></div>}

          {
            (selectedAddon && selectedAddon.addon_product_template_no != '') &&
            <div style={{ marginTop: '10px' }}>
              <div className="title">{t('addonTypes.' + selectedAddon.type.label)}</div>
              <div className="price-row"><span className="text">{t('common.monthlyPrice')}</span> <b>{selectedAddon.monthly_price} {t('common.€/month')}</b></div>
            </div>
          }
          <div className="title border">{t('phaseFour.total')}</div>
          <div className="total-row" dangerouslySetInnerHTML={{
            __html: t('phaseFour.priceTotal', {
              monthlyPrice: (discountRow && contractPeriod && discountRow.duration == contractPeriod) ? (selectedAddon?.monthly_price ?? 0) : monthlyPrice,
              energyPrice: `${selectedProduct?.name.includes('rssis') ? 'SPOT + ' : ''} ${selectedProduct?.energy_price ?? 0}`,
              estimate: selectedProduct?.name.includes('Joustos') ? ` + ${t('common.consumptionImpact').toLowerCase()}` : ''
            })
          }}></div>

          {(selectedProduct && selectedProduct.summary_label) && <div className="summary-label"><img src={IconCheckCircle} width={16} height={16} /><span dangerouslySetInnerHTML={{ __html: db_t(selectedProduct, 'summary_label') }}></span></div>}
        </ProductSummary >
      </WhiteContainer >

      <WhiteContainer>
        <h2>{t('phaseFour.addressTitle')}</h2>
        <p>{t('phaseFour.fillAddressFieldsText')}</p>
        <AddressForm type="location" validationErrors={formErrors} />

        <label style={{ marginTop: '10px' }} className="checkbox-label">
          <input type="checkbox" name="separated-invoicing-address" checked={formValues['separated-invoicing-address'] == 'on'} onChange={handleFormChange} />
          <span className="description">{t('phaseFour.separateInvoiceAddress')}</span>
        </label >
      </WhiteContainer >

      {
        formValues['separated-invoicing-address'] == 'on' && <WhiteContainer>
          <h2>{t('phaseFour.invoiceAndMailingAddress')}</h2>
          <AddressForm type="invoice" disableAddressCheck validationErrors={formErrors} />
        </WhiteContainer>
      }

      {(formValues['location-status'] === 'confirmed' || formValues['location-status'] === 'skipped') &&
        <React.Fragment>
          <WhiteContainer>
            <h2>{t('phaseFour.customerInfo')}</h2>
            <Grid>
              <TextInput
                required
                label={t('phaseFour.email')}
                fieldName="email"
                type="email"
                onChange={handleFormChange}
                value={formValues['email']}
                error={formErrors['email']}
              />
              <TextInput
                required
                label={t('phaseFour.phone')}
                fieldName="phone"
                type="tel"
                onChange={handleFormChange}
                value={formValues['phone']}
                error={formErrors['phone']}
              />
            </Grid>
          </WhiteContainer >

          <WhiteContainer>
            <h2>{t('phaseFour.contractInfo')}</h2>
            <label>{t('phaseFour.contract')}</label>
            <SummaryLabels className={formErrors['contract-type'] ? 'invalid' : ''}>
              {contractTypes.map((option) => (
                <label
                  htmlFor={option.value}
                  className="border radio-label"
                  key={option.value}>

                  <div className="header">
                    <input
                      type="radio"
                      id={option.value}
                      name="contract-type"
                      value={option.value}
                      checked={option.value === formValues['contract-type']}
                      onChange={handleFormChange}
                    />
                    <div className="title">{t(option.label)}</div>
                  </div >
                  <div className="description">{t(option.description)}</div>
                </label >
              ))}
            </SummaryLabels>

            <div>
              <DateInput
                label={t('phaseFour.contractStartDate')}
                name="contract-startdate"
                value={formValues['contract-startdate']}
                onChange={handleFormChange}
                min={minDate}
                max={maxDate}
                error={formErrors['contract-startdate']}
              />
            </div>

            <ContractTypeText updateMinDate={setMinDate} updateMaxDate={setMaxDate} />

            <label>{t('phaseFour.invoiceMethod')}</label>
            <SummaryLabels className={formErrors['invoice-method'] ? 'invalid' : ''}>
              {invoiceTypes.map((option) => (
                <label
                  htmlFor={option.value}
                  key={option.value} className="radio-label row">

                  <div className="header">
                    <input
                      type="radio"
                      id={option.value}
                      name="invoice-method"
                      value={option.value}
                      checked={option.value === formValues['invoice-method']}
                      onChange={handleFormChange}
                    />
                    <div className="description">{t(option.label)}</div>
                  </div>
                  {
                    option.value === 'einvoice' && formValues['invoice-method'] === 'einvoice' && (
                      <div className="description">
                        <p>{t('phaseFour.eInvoiceBank')}</p>
                        <select name="einvoice-bank" onChange={handleFormChange} value={formValues['einvoice-bank']} className={showErrors && formErrors['einvoice-bank'] ? 'invalid' : ''}>
                          <option value="">{t('phaseFour.chooseBank')}</option>
                          {einvoiceBanks.map((bank) => (
                            <option key={bank} value={bank}>{bank}</option>
                          ))}
                        </select>
                      </div>
                    )
                  }
                </label>
              ))}
            </SummaryLabels>

          </WhiteContainer>

          <WhiteContainer>
            <Terms showError={!!formErrors['accept-terms']} />
          </WhiteContainer>

          {
            showButtons && <Actions>
              <button
                type="button"
                id="confirm-order"
                className={"primary purple" + (Object.keys(allFormErrors).length === 0 ? '' : ' disabled')}
                onClick={confirmClick}>
                {t('phaseFour.confirmOrder')}
              </button>
            </Actions>
          }

        </React.Fragment>}
    </>
  )
}

export default PhaseFour;
